import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Heart, ArrowLeft, Mail, Lock, User, MapPin, Check, X, Calendar, Users, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { patientRegistration } from '../../redux/patientSlice';
import { checkEmailExists, sendOtp, verifyOtp } from '../../redux/authSlice';
import { getEmailName } from '../../redux/ctgSlice';
import { docPatCreationCountryList } from '../../redux/masterSlice';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import CountryTimeZoneSelect from '../../components/common/CountryTimeZoneSelect';
import GenderSelect from '../../components/common/GenderSelect';
import { getGenderOptions, formatFirstName, formatSurname } from '../../utils/personFields';

export default function PatientRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['register', 'addPatient', 'common', 'patientRegister']);
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.patient);
  const { emailCheckLoading, sendOtpLoading, verifyOtpLoading } = useSelector((state) => state.auth);
  const { countryTimezoneList } = useSelector((state) => state.master);
  const axiosInstance = useAxiosAuth();

  // Extract invitationCode from URL query parameters using location.search
  const searchParams = new URLSearchParams(location.search);
  const invitationCode = searchParams.get('invitationCode');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [charLimitReached, setCharLimitReached] = useState({});
  const [emailStatus, setEmailStatus] = useState(null); // null: not checked, true: available, false: exists
  const emailCheckTimeoutRef = useRef(null);
  const lastCheckedEmailRef = useRef(''); // Track last checked email
  const isEmailCheckInProgressRef = useRef(false); // Track if email check is in progress
  const [isEmailDisabled, setIsEmailDisabled] = useState(false); // Track if email is disabled due to invitation
  const hasCalledInvitationAPIRef = useRef(false); // Track if invitation API has been called

  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    gender: '',
    dob: '',
    countryId: '',
    timeZone: '',
    password: '',
    confirmPassword: '',
  });

  // --- Email OTP state ---
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpVerified, setEmailOtpVerified] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState('');
  const [emailOtpTimer, setEmailOtpTimer] = useState(0);
  const emailOtpTimerRef = useRef(null);
  const emailOtpVerifyingRef = useRef(false);

  // Fetch countries on mount
  useEffect(() => {
    dispatch(docPatCreationCountryList());
  }, [dispatch]);

  const resetOtpEmailState = useCallback(() => {
    setEmailOtpSent(false);
    setEmailOtpCode('');
    setEmailOtpVerified(false);
    setEmailOtpError('');
    setEmailOtpTimer(0);
    if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
    emailOtpVerifyingRef.current = false;
  }, []);

  const startOtpTimer = () => {
    if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
    setEmailOtpTimer(60);
    emailOtpTimerRef.current = setInterval(() => {
      setEmailOtpTimer(prev => {
        if (prev <= 1) { clearInterval(emailOtpTimerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendEmailOtp = async () => {
    setEmailOtpError('');
    try {
      const result = await dispatch(sendOtp({ email: formData.email.trim(), axiosInstance })).unwrap();
      if (result?.success) {
        setEmailOtpSent(true);
        setEmailOtpCode('');
        setEmailOtpVerified(false);
        startOtpTimer();
      }
    } catch (err) {
      setEmailOtpError(typeof err === 'string' ? err : t('patientRegister.failedToSendOtp', { ns: 'patientRegister' }));
    }
  };

  const handleVerifyEmailOtp = useCallback(async (code) => {
    const otpVal = code ?? emailOtpCode;
    if (emailOtpVerifyingRef.current || otpVal.length !== 6) return;
    emailOtpVerifyingRef.current = true;
    setEmailOtpError('');
    try {
      const result = await dispatch(verifyOtp({ email: formData.email.trim(), otpCode: otpVal, axiosInstance })).unwrap();
      if (result?.success) {
        setEmailOtpVerified(true);
        setErrors(prev => ({ ...prev, email: '' }));
        if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
        setEmailOtpTimer(0);
      } else {
        setEmailOtpError(t('patientRegister.invalidOtp', { ns: 'patientRegister' }));
      }
    } catch (err) {
      setEmailOtpError(typeof err === 'string' ? err : t('patientRegister.invalidOtp', { ns: 'patientRegister' }));
    } finally {
      emailOtpVerifyingRef.current = false;
    }
  }, [emailOtpCode, dispatch, axiosInstance, formData.email]);

  // Cleanup timer on unmount
  useEffect(() => { return () => { if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current); }; }, []);

  // Store dispatch and axiosInstance in refs to avoid dependency issues
  const dispatchRef = useRef(dispatch);
  const axiosInstanceRef = useRef(axiosInstance);

  // Update refs when values change
  useEffect(() => {
    dispatchRef.current = dispatch;
    axiosInstanceRef.current = axiosInstance;
  }, [dispatch, axiosInstance]);

  // Proper email regex: allows letters, numbers, dots, hyphens, underscores, and plus signs in local part
  const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Fetch invitation data if invitationCode is present in URL
  useEffect(() => {
    const fetchInvitationData = async () => {
      // Only call API once using ref check
      if (invitationCode && !hasCalledInvitationAPIRef.current) {
        hasCalledInvitationAPIRef.current = true; // Mark as called immediately

        const resultAction = await dispatch(getEmailName({ invitationCode }));

        if (getEmailName.fulfilled.match(resultAction) && resultAction.payload?.data) {
          const { email, name } = resultAction.payload.data;

          // Update form data with fetched email and name
          setFormData((prev) => ({
            ...prev,
            email: email || '',
            name: name || '',
          }));

          // Disable email field
          setIsEmailDisabled(true);

          // Trigger email check if email is fetched
          if (email && emailRegex.test(email)) {
            lastCheckedEmailRef.current = '';
            isEmailCheckInProgressRef.current = true;

            const emailCheckAction = await dispatchRef.current(checkEmailExists({
              email: email,
              axiosInstance: axiosInstanceRef.current
            }));

            if (checkEmailExists.fulfilled.match(emailCheckAction)) {
              setEmailStatus(!emailCheckAction.payload.exists);
            }

            isEmailCheckInProgressRef.current = false;
            lastCheckedEmailRef.current = email;
          }
        } else if (getEmailName.rejected.match(resultAction)) {
          toast.warning(t('patientRegister.invalidInvitationCode', { ns: 'patientRegister' }));
        }
      }
    };

    fetchInvitationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationCode]);

  // Debounced email check
  useEffect(() => {
    // Clear previous timeout
    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
    }

    const email = formData.email?.trim() || '';

    // If email is empty or invalid format, reset status
    if (!email || !emailRegex.test(email)) {
      setEmailStatus(null);
      lastCheckedEmailRef.current = '';
      return;
    }

    // If email hasn't changed or check is in progress, don't check again
    if (lastCheckedEmailRef.current === email || isEmailCheckInProgressRef.current) {
      return;
    }

    // Set new timeout for debounced check (500ms delay)
    emailCheckTimeoutRef.current = setTimeout(async () => {
      const trimmedEmail = formData.email.trim();

      // Double check email is still valid and hasn't changed during debounce
      if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedEmailRef.current === trimmedEmail || isEmailCheckInProgressRef.current) {
        return;
      }

      // Mark check as in progress and update last checked email BEFORE making the API call
      isEmailCheckInProgressRef.current = true;
      lastCheckedEmailRef.current = trimmedEmail;

      try {
        const resultAction = await dispatchRef.current(checkEmailExists({
          email: trimmedEmail,
          axiosInstance: axiosInstanceRef.current
        }));

        // Only update status if the email hasn't changed during the API call
        if (formData.email.trim() === trimmedEmail) {
          if (checkEmailExists.fulfilled.match(resultAction)) {
            // exists: true means email already exists (not available)
            // exists: false means email doesn't exist (available)
            setEmailStatus(!resultAction.payload.exists);
          } else {
            setEmailStatus(null);
            lastCheckedEmailRef.current = ''; // Reset on error
          }
        }
      } catch (error) {
        console.error('Error checking email:', error);
        if (formData.email.trim() === trimmedEmail) {
          setEmailStatus(null);
          lastCheckedEmailRef.current = ''; // Reset on error
        }
      } finally {
        isEmailCheckInProgressRef.current = false;
      }
    }, 500);

    // Cleanup function
    return () => {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.email]);

  const validatePassword = (pwd) => {
    const minLength = pwd.length >= 8;
    const maxLength = pwd.length <= 25;
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

    if (!minLength) return t('patientRegister.passwordMinLength', { ns: 'patientRegister' });
    if (!maxLength) return t('validation.password_too_long_25', { ns: 'common' });
    if (!hasUpperCase) return t('patientRegister.passwordUppercase', { ns: 'patientRegister' });
    if (!hasLowerCase) return t('patientRegister.passwordLowercase', { ns: 'patientRegister' });
    if (!hasNumber) return t('patientRegister.passwordNumber', { ns: 'patientRegister' });
    if (!hasSpecialChar) return t('patientRegister.passwordSpecialChar', { ns: 'patientRegister' });
    return '';
  };

  const getPasswordStrength = (pwd) => {
    if (!pwd) return '';
    const minLength = pwd.length >= 8;
    const maxLength = pwd.length <= 25;
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

    // Password is strong if it meets all requirements
    if (minLength && maxLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar) {
      return 'strong';
    }
    return 'weak';
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    // Use UTC getters for birthDate to avoid timezone shift on DOB
    let age = today.getFullYear() - birthDate.getUTCFullYear();
    const monthDiff = today.getMonth() - birthDate.getUTCMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
      age--;
    }
    return age;
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      newErrors.name = t('patientRegister.nameRequired', { ns: 'patientRegister' });
    } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(trimmedName)) {
      newErrors.name = t('patientRegister.nameInvalidChars', { ns: 'patientRegister' });
    } else if (trimmedName.length < 2) {
      newErrors.name = t('patientRegister.nameMinLength', { ns: 'patientRegister' });
    } else if (trimmedName.length > 20) {
      newErrors.name = t('patientRegister.nameMaxLength', { ns: 'patientRegister' });
    } else if (/^\s+$/.test(formData.name)) {
      newErrors.name = t('patientRegister.nameOnlySpaces', { ns: 'patientRegister' });
    }

    // Surname validation
    if (!formData.surname.trim()) {
      newErrors.surname = t('patientRegister.surnameRequired', { ns: 'patientRegister', defaultValue: 'Surname is required' });
    } else if (/^\s+$/.test(formData.surname)) {
      newErrors.surname = t('patientRegister.surnameOnlySpaces', { ns: 'patientRegister', defaultValue: 'Surname cannot be only spaces' });
    }

    // Email validation
    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      newErrors.email = t('patientRegister.emailRequired', { ns: 'patientRegister' });
    } else {
      // Check total length (RFC 5321: max 254 characters)
      if (trimmedEmail.length > 254) {
        newErrors.email = t('patientRegister.emailTooLong', { ns: 'patientRegister' });
      } else {
        // Split email into local and domain parts
        const parts = trimmedEmail.split('@');
        if (parts.length !== 2) {
          newErrors.email = t('patientRegister.invalidEmailFormat', { ns: 'patientRegister' });
        } else {
          const [localPart, domainPart] = parts;
          // Local part (before @) should be max 64 characters (RFC 5321)
          if (localPart.length > 64) {
            newErrors.email = t('validation.email_local_part_too_long', { ns: 'common' });
          } else if (localPart.length === 0) {
            newErrors.email = t('patientRegister.invalidEmailFormat', { ns: 'patientRegister' });
          } else if (domainPart.length === 0 || !domainPart.includes('.')) {
            newErrors.email = t('patientRegister.invalidEmailFormat', { ns: 'patientRegister' });
          } else if (domainPart.length > 253) {
            newErrors.email = t('validation.email_domain_part_too_long', { ns: 'common' });
          } else if (!emailRegex.test(trimmedEmail)) {
            newErrors.email = t('patientRegister.invalidEmailFormat', { ns: 'patientRegister' });
          } else if (emailStatus === false) {
            newErrors.email = t('patientRegister.emailAlreadyExists', { ns: 'patientRegister' });
          } else if (emailStatus === null && !emailCheckLoading) {
            newErrors.email = t('patientRegister.pleaseVerifyEmail', { ns: 'patientRegister' });
          } else if (emailStatus === true && !isEmailDisabled && !emailOtpVerified) {
            newErrors.email = t('patientRegister.pleaseVerifyEmailOtp', { ns: 'patientRegister' });
          }
        }
      }
    }

    // Gender validation
    if (!formData.gender) {
      newErrors.gender = t('patientRegister.genderRequired', { ns: 'patientRegister' });
    } else {
      // GenderSelect stores the option value ('male' | 'female' | 'Not_Communicated' | 'other'),
      // so validate against those values — not the translated labels — to avoid rejecting a
      // valid selection (e.g. value 'female' vs label 'Female').
      const validGenders = getGenderOptions(t).map((o) => o.value);
      if (!validGenders.includes(formData.gender)) {
        newErrors.gender = t('patientRegister.selectValidGender', { ns: 'patientRegister' });
      }
    }

    // DOB validation
    if (!formData.dob) {
      newErrors.dob = t('patientRegister.dobRequired', { ns: 'patientRegister' });
    } else {
      const dobDate = new Date(formData.dob);
      const today = new Date();

      if (isNaN(dobDate.getTime())) {
        newErrors.dob = t('patientRegister.invalidDateFormat', { ns: 'patientRegister' });
      } else if (dobDate > today) {
        newErrors.dob = t('patientRegister.dobFuture', { ns: 'patientRegister' });
      } else if (dobDate.getFullYear() < 1900) {
        newErrors.dob = t('patientRegister.dobAfter1900', { ns: 'patientRegister' });
      } else {
        const age = calculateAge(formData.dob);
        if (age < 0) {
          newErrors.dob = t('patientRegister.invalidDob', { ns: 'patientRegister' });
        } else if (age > 150) {
          newErrors.dob = t('patientRegister.enterValidDob', { ns: 'patientRegister' });
        }
      }
    }

    // Country validation
    if (!formData.countryId) {
      newErrors.countryId = t('patientRegister.countryRequired', { ns: 'patientRegister' });
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('validation.password_required', { ns: 'common' });
    } else {
      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        newErrors.password = passwordError;
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('patientRegister.pleaseConfirmPassword', { ns: 'patientRegister' });
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('patientRegister.passwordsDoNotMatch', { ns: 'patientRegister' });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const maxLengths = { name: 20, surname: 25 };
    let processedValue = value;

    // First name: only the first character uppercased.
    if (name === 'name' && processedValue.length > 0) {
      processedValue = formatFirstName(processedValue);
    }
    // Surname (family name): always uppercase.
    if (name === 'surname' && processedValue.length > 0) {
      processedValue = formatSurname(processedValue);
    }

    // Restrict name and surname to letters, spaces, hyphens, and apostrophes only
    if ((name === 'name' || name === 'surname') && processedValue && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(processedValue)) return;

    // Restrict email to valid email characters only (letters, numbers, dots, hyphens, underscores, plus, @)
    if (name === 'email' && processedValue) {
      // Allow only valid email characters: letters, numbers, dots, hyphens, underscores, plus, and @
      if (!/^[a-zA-Z0-9._+-@]*$/.test(processedValue)) return;
    }

    // Reset email status and tracking when email changes
    if (name === 'email') {
      setEmailStatus(null);
      lastCheckedEmailRef.current = '';
      isEmailCheckInProgressRef.current = false;
      resetOtpEmailState();
    }

    // Check if character limit is reached
    if (maxLengths[name] && processedValue.length >= maxLengths[name]) {
      setCharLimitReached({ ...charLimitReached, [name]: true });
    } else {
      setCharLimitReached({ ...charLimitReached, [name]: false });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    // Real-time validation for password match
    if (name === 'confirmPassword' && formData.password && value) {
      if (formData.password !== value) {
        setErrors((prev) => ({ ...prev, confirmPassword: t('patientRegister.passwordsDoNotMatch', { ns: 'patientRegister' }) }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: '' }));
      }
    }

    // Real-time validation for password field when confirmPassword exists
    if (name === 'password' && formData.confirmPassword && value) {
      if (value !== formData.confirmPassword) {
        setErrors((prev) => ({ ...prev, confirmPassword: t('patientRegister.passwordsDoNotMatch', { ns: 'patientRegister' }) }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Ensure email is validated before registration
    if (emailStatus !== true) {
      toast.warning(t('patientRegister.verifyEmailToProceed', { ns: 'patientRegister' }));
      return;
    }
    // Ensure email OTP is verified (skip for invitation flow)
    if (!isEmailDisabled && !emailOtpVerified) {
      toast.warning(t('patientRegister.verifyEmailOtpBeforeRegistering', { ns: 'patientRegister' }));
      return;
    }

    // Convert gender from translation to payload format (same as AddPatient modal)
    const genderPayload = formData.gender === t('addPatient.male')
      ? 'male'
      : formData.gender === t('addPatient.female')
        ? 'female'
        : formData.gender === t('common.gender.notCommunicated') || formData.gender === t('addPatient.Not_Communicated')
          ? 'Not_Communicated'
          : formData.gender === t('addPatient.others')
            ? 'other'
            : formData.gender;

    try {
      // Trim all string fields before submission
      const registrationData = {
        name: formData.name.trim(),
        surname: formData.surname.trim(),
        email: formData.email.trim(),
        gender: genderPayload,
        dob: formData.dob,
        countryId: formData.countryId,
        timeZone: formData.timeZone,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        emailVerified: !!(emailOtpVerified || isEmailDisabled),
      };

      // Add invitationCode only if it exists in the URL
      if (invitationCode) {
        registrationData.invitationCode = invitationCode;
      }

      const resultAction = await dispatch(patientRegistration(registrationData));

      if (patientRegistration.fulfilled.match(resultAction)) {
        toast.success(t('patientRegister.registrationSuccess', { ns: 'patientRegister' }));
        setTimeout(() => navigate('/login'), 2000);
      } else {
        const errorMessage = resultAction.payload?.error?.response?.data?.message?.en
          || resultAction.payload?.error?.message
          || t('patientRegister.registrationFailed', { ns: 'patientRegister' });
        toast.warning(errorMessage);
      }
    } catch (error) {
      toast.warning(t('patientRegister.unexpectedError', { ns: 'patientRegister' }));
    }
  };

  return (
    <div data-testid="patient-register-container" className="min-h-screen w-full bg-white overflow-hidden flex items-center justify-center relative py-8 px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-20 w-72 h-72 bg-[#4ABDBD]/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-20 w-72 h-72 bg-[#3A9B9B]/10 rounded-full blur-3xl"></div>
      </div>

      {/* Back Button - Top Left Corner */}
      <button
        data-testid="patient-register-back-button"
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 sm:left-8 z-20 px-3 py-2 border-2 border-[#4ABDBD] text-[#4ABDBD] font-semibold rounded-lg hover:bg-[#f0faf9] transition flex items-center justify-center gap-2 cursor-pointer"
      >
        <ArrowLeft className="w-5 h-5" />
        {t('patientRegister.back', { ns: 'patientRegister' })}
      </button>

      <div className="relative z-10 w-full max-w-6xl mt-16 sm:mt-8">
        <div>
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            {/* Left Column - Intro */}
            <div data-testid="patient-register-intro-section" className="lg:w-1/3 flex flex-col gap-4 text-center lg:text-left">
              {/* <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-[#4ABDBD] to-[#3A9B9B] flex items-center justify-center shadow-lg">
                <Heart className="text-white w-8 h-8" />
              </div> */}
              <div>
                <h1 data-testid="patient-register-title" className="gradient-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-2 pb-2">
                  {t('patientRegister.patientRegistration', { ns: 'patientRegister' })}
                </h1>
                <p data-testid="patient-register-description" className="text-gray-500 text-sm mt-2">
                  {t('patientRegister.description', { ns: 'patientRegister' })}
                </p>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="lg:w-2/3">
              <form data-testid="patient-register-form" onSubmit={handleRegister} className="flex flex-col gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 1. Gender */}
                  <div data-testid="patient-register-gender-field-container">
                    <label data-testid="patient-register-gender-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('patientRegister.gender', { ns: 'patientRegister' })} <span className="text-red-500">*</span>
                    </label>
                    <GenderSelect
                      value={formData.gender}
                      onChange={(val) => {
                          setFormData(prev => ({ ...prev, gender: val }));
                          setErrors(prev => ({ ...prev, gender: null }));
                      }}
                      options={getGenderOptions(t)}
                      placeholder={t('patientRegister.selectGender', { ns: 'patientRegister' })}
                      error={!!errors.gender}
                    />
                    {errors.gender && <p data-testid="patient-register-gender-error" className="text-red-500 text-xs mt-1">{errors.gender}</p>}
                  </div>

                  {/* 2. First Name */}
                  <div data-testid="patient-register-name-field-container">
                    <label data-testid="patient-register-name-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('patientRegister.name', { ns: 'patientRegister' })} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="patient-register-name-input"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        maxLength={20}
                        placeholder={t('register.enter_your_name')}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                      />
                    </div>
                    {charLimitReached.name && (
                      <p data-testid="patient-register-name-char-limit" className="text-xs text-amber-600 mt-1">{t('patientRegister.charLimitReached', { ns: 'patientRegister' })}</p>
                    )}
                    {errors.name && <p data-testid="patient-register-name-error" className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>

                  {/* 3. Surname */}
                  <div data-testid="patient-register-surname-field-container">
                    <label data-testid="patient-register-surname-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('patientRegister.surname', { ns: 'patientRegister', defaultValue: 'Surname' })} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${errors.surname ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'}`}>
                      <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="patient-register-surname-input"
                        type="text"
                        name="surname"
                        value={formData.surname}
                        onChange={handleInputChange}
                        maxLength={25}
                        placeholder={t('patientRegister.enterSurname', { ns: 'patientRegister', defaultValue: 'Enter surname' })}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm uppercase"
                      />
                    </div>
                    {errors.surname && <p data-testid="patient-register-surname-error" className="text-red-500 text-xs mt-1">{errors.surname}</p>}
                  </div>

                  {/* DOB */}
                  <div data-testid="patient-register-dob-field-container">
                    <label data-testid="patient-register-dob-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('patientRegister.dateOfBirth', { ns: 'patientRegister' })} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${errors.dob ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <Calendar className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="patient-register-dob-input"
                        type="date"
                        name="dob"
                        value={formData.dob}
                        onChange={handleInputChange}
                        min="1900-01-01"
                        max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().split('T')[0]; })()}
                        className="w-full bg-transparent focus:outline-none text-gray-800 text-sm"
                      />
                    </div>
                    {errors.dob && <p data-testid="patient-register-dob-error" className="text-red-500 text-xs mt-1">{errors.dob}</p>}
                  </div>

                  {/* Age (calculated, disabled) */}
                  <div data-testid="patient-register-age-field-container">
                    <label data-testid="patient-register-age-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('patientRegister.age', { ns: 'patientRegister', defaultValue: 'Age' })}
                    </label>
                    <div className="relative flex items-center border rounded-xl px-3 py-3 bg-gray-100 border-gray-200">
                      <Users className="text-gray-400 w-5 h-5 mr-2" />
                      <input
                        data-testid="patient-register-age-input"
                        type="text"
                        value={formData.dob ? (() => {
                          const today = new Date();
                          const birth = new Date(formData.dob);
                          // Use UTC getters for birthDate to avoid timezone shift on DOB
                          let age = today.getFullYear() - birth.getUTCFullYear();
                          const m = today.getMonth() - birth.getUTCMonth();
                          if (m < 0 || (m === 0 && today.getDate() < birth.getUTCDate())) age--;
                          return age >= 0 ? age : '';
                        })() : ''}
                        disabled
                        placeholder={t('patientRegister.agePlaceholder', { ns: 'patientRegister', defaultValue: 'Auto-calculated from DOB' })}
                        className="w-full bg-transparent focus:outline-none text-gray-600 placeholder-gray-400 text-sm cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div data-testid="patient-register-email-field-container">
                    <label data-testid="patient-register-email-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('patientRegister.email', { ns: 'patientRegister' })} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${emailOtpVerified
                      ? 'border-green-400 focus-within:ring-2 focus-within:ring-green-500'
                      : emailStatus === false
                        ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                        : emailStatus === true
                          ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                          : errors.email
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <Mail className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                      <input
                        data-testid="patient-register-email-input"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={t('register.enter_your_email')}
                        maxLength={254}
                        disabled={isEmailDisabled || emailOtpVerified}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                      />
                      {emailCheckLoading && (
                        <span data-testid="patient-register-email-loading" className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                      )}
                      {!emailCheckLoading && emailStatus === false && (
                        <X
                          data-testid="patient-register-email-clear-icon"
                          className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, email: '' }));
                            setEmailStatus(null);
                            setErrors(prev => ({ ...prev, email: '' }));
                            lastCheckedEmailRef.current = '';
                            resetOtpEmailState();
                          }}
                        />
                      )}
                      {!emailCheckLoading && emailStatus === true && !isEmailDisabled && !emailOtpVerified && (
                        <button
                          data-testid="patient-register-send-otp-button"
                          type="button"
                          onClick={handleSendEmailOtp}
                          disabled={sendOtpLoading || emailOtpSent}
                          className="ml-2 flex-shrink-0 text-xs px-2 py-1 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {sendOtpLoading ? '...' : emailOtpSent ? t('patientRegister.sent', { ns: 'patientRegister' }) : t('patientRegister.sendOtp', { ns: 'patientRegister' })}
                        </button>
                      )}
                      {(emailOtpVerified || (emailStatus === true && isEmailDisabled)) && (
                        <ShieldCheck data-testid="patient-register-email-verified-icon" className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                      )}
                    </div>
                    {emailOtpVerified && !isEmailDisabled && (
                      <p data-testid="patient-register-otp-verified-text" className="text-green-600 text-xs mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {t('patientRegister.emailOtpVerified', { ns: 'patientRegister' })}</p>
                    )}
                    {emailOtpSent && !emailOtpVerified && !isEmailDisabled && (
                      <div data-testid="patient-register-otp-section" className="mt-2 space-y-1">
                        <div className="flex items-center gap-2">
                          <input
                            data-testid="patient-register-otp-input"
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={emailOtpCode}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setEmailOtpCode(val);
                              setEmailOtpError('');
                              if (val.length === 6) handleVerifyEmailOtp(val);
                            }}
                            placeholder={t('patientRegister.enterOtp', { ns: 'patientRegister' })}
                            className="flex-1 min-w-0 border border-teal-300 rounded-lg px-3 py-1.5 text-sm text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-400"
                          />
                          <button
                            data-testid="patient-register-verify-otp-button"
                            type="button"
                            onClick={() => handleVerifyEmailOtp()}
                            disabled={verifyOtpLoading || emailOtpCode.length !== 6}
                            className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {verifyOtpLoading ? '...' : t('patientRegister.verify', { ns: 'patientRegister' })}
                          </button>
                        </div>
                        <div>
                          {emailOtpTimer > 0 ? (
                            <span data-testid="patient-register-otp-timer" className="text-xs text-gray-500">{t('patientRegister.resendIn', { ns: 'patientRegister', seconds: emailOtpTimer })}</span>
                          ) : (
                            <button data-testid="patient-register-resend-otp-button" type="button" onClick={handleSendEmailOtp} disabled={sendOtpLoading} className="text-xs text-teal-600 font-semibold hover:underline disabled:opacity-50">{t('patientRegister.resendOtp', { ns: 'patientRegister' })}</button>
                          )}
                        </div>
                        {emailOtpError && <p data-testid="patient-register-otp-error" className="text-red-500 text-xs">{emailOtpError}</p>}
                      </div>
                    )}
                    {errors.email && <p data-testid="patient-register-email-error" className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    {!emailCheckLoading && emailStatus === false && !errors.email && (
                      <p data-testid="patient-register-email-exists-error" className="text-red-500 text-xs mt-1">{t('patientRegister.emailAlreadyExists', { ns: 'patientRegister' })}</p>
                    )}
                  </div>

                  {/* Country & Timezone */}
                  <div data-testid="patient-register-country-field-container" className="md:col-span-2">
                    <label data-testid="patient-register-country-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('patientRegister.country', { ns: 'patientRegister' })} <span className="text-red-500">*</span>
                    </label>
                    <CountryTimeZoneSelect
                      countryTimezoneList={countryTimezoneList}
                      countryId={formData.countryId}
                      timeZone={formData.timeZone}
                      onChange={({ countryId, timeZone }) => {
                          setFormData(prev => ({ ...prev, countryId, timeZone }));
                          setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                      }}
                      placeholder={t('patientRegister.selectCountry', { ns: 'patientRegister' })}
                      error={!!errors.countryId || !!errors.timeZone}
                    />
                    {errors.countryId && <p data-testid="patient-register-country-error" className="text-red-500 text-xs mt-1">{errors.countryId}</p>}
                    {errors.timeZone && <p data-testid="patient-register-timezone-error" className="text-red-500 text-xs mt-1">{errors.timeZone}</p>}
                  </div>

                  {/* Password */}
                  <div data-testid="patient-register-password-field-container">
                    <label data-testid="patient-register-password-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('patientRegister.password', { ns: 'patientRegister' })} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${errors.password ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <Lock className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="patient-register-password-input"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder={t('register.create_password')}
                        maxLength={25}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                      />
                      <button
                        data-testid="patient-register-toggle-password-button"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="ml-2 focus:outline-none hover:scale-110 transition-transform"
                      >
                        {showPassword ? (
                          <Eye className="text-[#4ABDBD] w-5 h-5" />
                        ) : (
                          <EyeOff className="text-[#4ABDBD] w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && <p data-testid="patient-register-password-error" className="text-red-500 text-xs mt-1">{errors.password}</p>}
                    {formData.password && !errors.password && (
                      <p data-testid="patient-register-password-strength-text" className={`text-xs mt-1 ${getPasswordStrength(formData.password) === 'strong'
                        ? 'text-green-600'
                        : 'text-amber-600'
                        }`}>
                        {getPasswordStrength(formData.password) === 'strong'
                          ? t('patientRegister.passwordStrengthStrong', { ns: 'patientRegister' })
                          : t('patientRegister.passwordStrengthWeak', { ns: 'patientRegister' })}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div data-testid="patient-register-confirm-password-field-container">
                    <label data-testid="patient-register-confirm-password-label" className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('patientRegister.confirmPassword', { ns: 'patientRegister' })} <span className="text-red-500">*</span>
                    </label>
                    <div className={`relative flex items-center border rounded-xl px-3 py-3 bg-white ${errors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-200 focus-within:ring-2 focus-within:ring-[#4ABDBD]'
                      }`}>
                      <Lock className="text-[#4ABDBD] w-5 h-5 mr-2" />
                      <input
                        data-testid="patient-register-confirm-password-input"
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder={t('register.confirm_password')}
                        maxLength={25}
                        className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                      />
                      <button
                        data-testid="patient-register-toggle-confirm-password-button"
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="ml-2 focus:outline-none hover:scale-110 transition-transform"
                      >
                        {showConfirmPassword ? (
                          <Eye className="text-[#4ABDBD] w-5 h-5" />
                        ) : (
                          <EyeOff className="text-[#4ABDBD] w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && <p data-testid="patient-register-confirm-password-error" className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                  </div>
                </div>

                <button
                  data-testid="patient-register-submit-button"
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3 sm:py-4 font-bold rounded-xl text-white bg-linear-to-r from-[#4ABDBD] to-[#3A9B9B] hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 ${isLoading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:scale-[1.01]'
                    }`}
                >
                  {isLoading ? (
                    <>
                      <span className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      {t('patientRegister.registering', { ns: 'patientRegister' })}
                    </>
                  ) : (
                    <>
                      <Heart className="w-5 h-5" />
                      {t('patientRegister.register', { ns: 'patientRegister' })}
                    </>
                  )}
                </button>

                <p data-testid="patient-register-already-have-account-text" className="text-center text-sm text-gray-600">
                  {t('patientRegister.alreadyHaveAccount', { ns: 'patientRegister' })}{' '}
                  <button data-testid="patient-register-login-button" type="button" onClick={() => navigate('/login')} className="text-[#4ABDBD] font-semibold hover:underline">
                    {t('patientRegister.login', { ns: 'patientRegister' })}
                  </button>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
